import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Instagram,
  Facebook,
  MessageCircle,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      icon: Instagram,
      href: "https://www.instagram.com/myos.makeoverstudio/",
      label: "Instagram",
    },
    {
      icon: Facebook,
      href: "https://www.facebook.com/profile.php?id=61573696574718",
      label: "Facebook",
    },
    {
      icon: MessageCircle,
      href: "https://wa.me/916238426642",
      label: "WhatsApp",
    },
  ];

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Gallery", path: "/gallery" },
    { name: "Team", path: "/team" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <footer className="bg-gradient-to-br from-dark-800 to-dark-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Salon Info */}
          <div className="space-y-4">
            <div className="w-20 h-16 lg:w-24 lg:h-20">
              <img
                src="/myos_makeover.svg"
                alt="MYOS Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-300 leading-relaxed">
              Premium unisex salon with branches in Payyoli and Koyilandy,
              Calicut. We specialize in creating beautiful styles that reflect
              your unique personality.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary-500 transition-colors"
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">Our Services</h4>
            <ul className="space-y-2 text-gray-300">
              <li>Haircut & Beard Styling</li>
              <li>Spa & Facial</li>
              <li>Grooming</li>
              <li>Hair Coloring</li>
              <li>Bridal Services</li>
              <li>Makeup</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">Our Branches</h4>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 font-medium">Payyoli Branch</p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Near Anand Hospital, Payyoli, Kerala 673522
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 pl-8">
                  <Phone className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <a
                    href="tel:+916238426642"
                    className="text-gray-300 hover:text-primary-400 transition-colors text-sm"
                  >
                    +91 62384 26642
                  </a>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 font-medium">Koyilandy Branch</p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      E, 10, 128/F, PANTHALAYANI Madhavi Building, Top of
                      Dhanlaxmi Bank Building No-10 - 128/B, 10 - 128/C, 10 -
                      128/D, 10 - 128, Koyilandy, Kerala 673305
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 pl-8">
                  <Phone className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <a
                    href="tel:+916238626642"
                    className="text-gray-300 hover:text-primary-400 transition-colors text-sm"
                  >
                    +91 62386 26642
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a
                  href="mailto:myosmakeoverstudio@gmail.com"
                  className="text-gray-300 hover:text-primary-400 transition-colors"
                >
                  myosmakeoverstudio@gmail.com
                </a>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">
                    Every Day: 10:30 AM - 10:00 PM
                  </p>
                  <p className="text-gray-400 text-sm">
                    No Off Days - Always Open!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} myosmakeoverstudio. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-primary-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-primary-400 transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
